<script setup lang="ts">
import { ref } from 'vue';
import { useForm } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/zod';
import { useConfirm } from 'primevue/useconfirm';
import ConfirmDialog from 'primevue/confirmdialog';
import {
  useAccountDetails,
  useToastNotifications,
} from '@apparatix/composables';
import { accountDetailSchema } from '@apparatix/schemas/AccountDetail';
import { useAccountStore } from '@apparatix/stores/useAccountStore';
import AccountDetailEditorForm from '@apparatix/leads/AccountDetailEditorForm.vue';
import AccountDetailViewForm from '@apparatix/leads/AccountDetailViewForm.vue';
import DetailEditorControls from '@apparatix/library/DetailEditorControls.vue';

const store = useAccountStore();
const confirm = useConfirm();
const { showSuccess } = useToastNotifications();

const getDefaultAddress = () => {
  if (!store.accountDetails.addresses[0]) {
    return [{
      addressLine1: '',
      addressLine2: '',
      city: '',
      region: '',
      postalCode: '',
    }];
  }
  return store.accountDetails.addresses;
};

const {
  defineField, meta, resetForm, errors: detailEditorErrors, values: detailEditorModel,
} = useForm({
  validationSchema: toTypedSchema(accountDetailSchema),
  initialValues: {
    id: store.accountDetails.id,
    owner: store.accountDetails.owner,
    name: store.accountDetails.name,
    legalName: store.accountDetails.legalName,
    industry: store.accountDetails.industry,
    source: store.accountDetails.source,
    markets: store.accountDetails.markets,
    label: store.accountDetails.label ?? null,
    office: store.accountDetails.office,
    phoneNumbers: store.accountDetails.phoneNumbers,
    webPages: store.accountDetails.webPages,
    addresses: getDefaultAddress(),
  },
});

const detail = useAccountDetails();

const [owner] = defineField('owner');
const [name] = defineField('name');
const [legalName] = defineField('legalName');
const [industry] = defineField('industry');
const [source] = defineField('source');
const [markets] = defineField('markets');
const [label] = defineField('label');
const [office] = defineField('office');
const [phoneNumbers] = defineField('phoneNumbers');
const [webPages] = defineField('webPages');
const [addresses] = defineField('addresses');

const isEditing = ref(false);
const isSaving = ref(false);

const onSave = async () => {
  isSaving.value = true;
  await detail.sendAccountUpdate(detailEditorModel);
  isSaving.value = false;
  showSuccess('Account Saved', 'Success');
  isEditing.value = false;
  resetForm({
    values: {
      id: store.accountDetails.id,
      owner: store.accountDetails.owner,
      name: store.accountDetails.name,
      legalName: store.accountDetails.legalName,
      industry: store.accountDetails.industry,
      source: store.accountDetails.source,
      markets: store.accountDetails.markets,
      label: store.accountDetails.label ?? null,
      office: store.accountDetails.office,
      phoneNumbers: store.accountDetails.phoneNumbers,
      webPages: store.accountDetails.webPages,
      addresses: getDefaultAddress(),
    },
  });
};

const onCancelEditing = () => {
  if (meta.value.dirty) {
    confirm.require({
      header: ' ',
      group: 'confirmDetailsClose',
      message: 'You have unsaved changes. Do you want to continue?',
      accept: () => onConfirmCancel(),
      acceptLabel: 'Yes',
      rejectLabel: 'No',
      rejectClass: 'p-button-secondary',
    });
    return;
  }
  onConfirmCancel();
};

const onConfirmCancel = () => {
  resetForm();
  isEditing.value = false;
};

</script>
<template>
  <DetailEditorControls
    header="Details"
    :is-editing="isEditing"
    :disable-save="Boolean(Object.keys(detailEditorErrors).length) || !meta.dirty || isSaving"
    @on:edit-click="isEditing = true"
    @on:cancel-click="onCancelEditing"
    @on:save-click="onSave"
  >
    <AccountDetailViewForm
      v-if="!isEditing"
      :account-details="detailEditorModel"
      :errors="detailEditorErrors"
    />
    <AccountDetailEditorForm
      v-if="isEditing"
      v-model="detailEditorModel"
      v-model:owner="owner"
      v-model:name="name"
      v-model:legal-name="legalName"
      v-model:industry="industry"
      v-model:source="source"
      v-model:markets="markets"
      v-model:label="label"
      v-model:office="office"
      v-model:phone-numbers="phoneNumbers"
      v-model:web-pages="webPages"
      v-model:addresses="addresses"
      :errors="detailEditorErrors"
    />
  </DetailEditorControls>
  <ConfirmDialog group="confirmDetailsClose" />
</template>
